import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { Header } from '@/components/header.tsx';
import { ImageCarousel } from '@/components/image-carousel.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { Pager } from '@/components/pager.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import { fetchCampaignItems } from '@/lib/campaign.api.ts';
import { MSG } from '@/lib/constants.ts';
import { campaignImages, formatPrice } from '@/lib/utils.ts';
import {
  MISSION_EMPTY_TEXT,
  MISSION_FILTERS,
  MISSION_PICK_BADGE,
  missionBoosted,
  missionRepeat,
  missionReward,
  todayPick,
} from '@/data/mission.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import type { DaoCampaign } from '@/types/dao.ts';
import type { PageProps } from '@/types/props.ts';

/**
 * 포인트 칩 (시안 `.reward-chip`).
 *
 * 금액은 `missionReward()` 가 서버와 같은 식으로 계산한다. 배수가 실제로 붙는
 * 동안에만 `×N` 을 덧붙인다 — 적혀 있다고 붙는 것이 아니라서, 붙지 않는 배수를
 * 보여 주면 받을 금액을 잘못 알려 주게 된다.
 */
const RewardChip: FunctionalComponent<{ campaign: DaoCampaign }> = ({ campaign }) => {
  const boosted = missionBoosted(campaign);

  /*
    ★ **이미 참여한 미션은 금액 대신 '완료'** 를 보여준다. (2026-09-16 요청)
      재참여 가능 시간이 될 때까지 유지된다. 하나하나 눌러 들어가 보지 않아도
      목록에서 바로 알 수 있어야 한다는 요청이다.

    ★ 반드시 `=== false` 로 본다. `availableToday` 는 **로그인했을 때만** 서버가
      채운다. 비로그인이면 키가 아예 없어 `undefined` 인데, `!campaign.availableToday`
      라고 적으면 **비로그인 사용자에게 모든 미션이 '완료' 로 보인다.**
  */
  if (campaign.availableToday === false) {
    return (
      <span class="reward-chip reward-chip--done">
        <img src="/images/coin_end.png" alt="" />
        완료
      </span>
    );
  }

  return (
    <span class={boosted ? 'reward-chip reward-chip--boost' : 'reward-chip'}>
      <img src="/images/point.png" alt="" />
      {formatPrice(missionReward(campaign))}
      {boosted && <b class="reward-chip-x">×{campaign.rewardMultiple}</b>}
    </span>
  );
};

/**
 * 미션 (시안 `_mission.html`).
 *
 * **목록은 실제 미션 API 를 쓴다** (`/api/campaign/items.json`). 공개·사용중이면서
 * 오늘이 기간 안인 미션만 내려오므로, 진행중인 미션이 없으면 비어 보인다.
 *
 * 탭은 **미션 형식**이다 — 음악(mp3) / 영상(유튜브). `MC_CAMPAIGN.F_TYPE`.
 *
 * 시안의 카드 정보는 하드코딩 9건이었지만 전부 미션 필드로 대체했다.
 *   제목  `song.songname` → 없으면 `openTitle`
 *   아티스트 `song.artistname`
 *   썸네일 `images[]` (여러 장이면 캐러셀)
 *   포인트 `reward × rewardMultiple`
 */
const Mission: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();

  /** 지금 탭의 미션들. 서버가 이미 형식으로 걸러서 준다 */
  const [campaigns, setCampaigns] = useState<DaoCampaign[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [filterId, setFilterId] = useState(MISSION_FILTERS[0].id);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * Today PICK 은 탭·페이지와 무관하게 **전체에서** 고른다.
   * 목록과 같은 응답을 쓰면 탭을 옮길 때마다 추천이 바뀌어 버린다.
   */
  const [allCampaigns, setAllCampaigns] = useState<DaoCampaign[]>([]);

  const filter = MISSION_FILTERS.find((f) => f.id === filterId) ?? MISSION_FILTERS[0];

  // 탭이나 페이지가 바뀔 때마다 그 몫만 받아온다.
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);

    void (async () => {
      try {
        const response = await fetchCampaignItems(page, filter.campaignTypes[0]);
        if (cancelled) return;

        if (!response.result) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          return;
        }
        setCampaigns(response.data?.items ?? []);
        setTotalPages(Math.max(1, response.data?.total_pages ?? 1));
      } catch {
        if (!cancelled) await dispatch(askAlert(MSG.apiFail));
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, filter.campaignTypes, page]);

  // Today PICK 용으로 전체를 한 번만 받아둔다. 탭을 옮겨도 추천은 그대로다.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await fetchCampaignItems(1);
        if (!cancelled && response.result) setAllCampaigns(response.data?.items ?? []);
      } catch {
        // 추천은 없어도 화면이 돌아간다. 조용히 넘어간다.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const visible = campaigns;

  /**
   * 시안의 '오늘의 추천 미션'. **한 건만** 올린다 — 슬라이드는 없앴다(2026-09-15).
   * 하나도 고르지 않았으면 목록 첫 건을 쓴다.
   */
  const pick = todayPick(allCampaigns);

  function goPlay(e: MouseEvent, seq: number | undefined) {
    e.preventDefault();
    if (seq) route(`/play/${seq}`);
  }

  function titleOf(campaign: DaoCampaign): string {
    return campaign.song?.songname || campaign.openTitle || campaign.title || '';
  }

  return (
    <div class="page">
      <Header />

      <div class="section">
        <div class="contents">
          {/*
            오늘의 추천 미션 — 한 건, 움직이지 않는다.
            ★ 카드 안 이미지도 **대표 이미지 한 장만** 쓴다. 이미지가 여러 장이면
              ImageCarousel 이 그 안에서 돌아가 '상단 슬라이드' 가 그대로 남는다.
          */}
          {pick && (
            <div class="mission-section">
              <a
                href={`/play/${pick.seq}`}
                class="mission-card"
                onClick={(e) => goPlay(e, pick.seq)}
              >
                <div class="mission-thumb">
                  <ImageCarousel
                    images={campaignImages(pick).slice(0, 1)}
                    alt={titleOf(pick)}
                    dots={false}
                  />
                  <span class="mission-badge">{MISSION_PICK_BADGE}</span>
                  <div class="mission-overlay">
                    <div class="mission-artist">{pick.song?.artistname}</div>
                    <div class="mission-title-row">
                      <div class="mission-title">{titleOf(pick)}</div>
                      <RewardChip campaign={pick} />
                    </div>
                  </div>
                </div>
              </a>
            </div>
          )}

          <div class="mgrid-filter">
            {MISSION_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                class={item.id === filterId ? 'mgrid-filter-item active' : 'mgrid-filter-item'}
                onClick={() => {
                  if (item.id === filterId) return;
                  // 탭을 옮기면 1페이지부터. 3페이지에서 옮겼는데 그 탭에 2페이지밖에
                  // 없으면 빈 화면이 된다.
                  setFilterId(item.id);
                  setPage(1);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div class="mgrid-section">
            {!loaded && <LoadingSpinner show />}

            {loaded && visible.length > 0 && filter.layout === 'music' && (
              <div class="music-list">
                {visible.map((campaign) => (
                  <a
                    key={campaign.seq}
                    href={`/play/${campaign.seq}`}
                    class="music-item"
                    onClick={(e) => goPlay(e, campaign.seq)}
                  >
                    <div class="music-thumb">
                      <ImageCarousel
                        images={campaignImages(campaign)}
                        alt={titleOf(campaign)}
                        dots={false}
                      />
                      <RewardChip campaign={campaign} />
                    </div>
                    <div class="music-info">
                      <div class="music-artist">{campaign.song?.artistname}</div>
                      <div class="music-title">{titleOf(campaign)}</div>
                      <div class="music-meta">
                        <div class="music-badge">{missionRepeat(campaign)}</div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {loaded && visible.length > 0 && filter.layout === 'reel' && (
              <div class="reel-list">
                {visible.map((campaign) => (
                  <a
                    key={campaign.seq}
                    href={`/play/${campaign.seq}`}
                    class="reel-item"
                    onClick={(e) => goPlay(e, campaign.seq)}
                  >
                    <div class="reel-thumb">
                      <ImageCarousel
                        images={campaignImages(campaign)}
                        alt={titleOf(campaign)}
                        dots={false}
                      />
                      <RewardChip campaign={campaign} />
                    </div>
                    <div class="reel-info">
                      <div class="reel-artist">{campaign.song?.artistname}</div>
                      <div class="reel-title">{titleOf(campaign)}</div>
                      <div class="reel-meta">
                        <div class="reel-badge">{missionRepeat(campaign)}</div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {loaded && visible.length === 0 && <p class="mgrid-empty">{MISSION_EMPTY_TEXT}</p>}

            {loaded && (
              <Pager
                page={page}
                totalPages={totalPages}
                onChange={(next) => {
                  setPage(next);
                  // 페이지를 넘기면 목록 위로 올려 준다. 안 그러면 아래쪽에 머물러
                  // 무엇이 바뀌었는지 알기 어렵다.
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}
          </div>
        </div>
      </div>

      <TabBar path="/mission" />
    </div>
  );
};

export default Mission;
